export const FACEBOOK_CLICK_ID_KEY = "fbc"

const AGE_KEY = "age"
const GENDER_KEY = "gender"
const TRY_FIRST_KEY = "tryFirst"
const ONBOARDING_VERSION_KEY = "onboardingVersion"
const ONBOARDING_CONTEXT_KEY = "onboardingContext"
const LOOKSMAXXING_CUSTOM_PURCHASE_PIXEL_KEY = "looksmaxxingCustomPurchasePixel"

export function addFacebookParamsToLocalStorage(locationSearch: string) {
  const urlSearchParams = new URLSearchParams(locationSearch)

  addFbcToLocalStorage(urlSearchParams)
}

function addFbcToLocalStorage(urlSearchParams: URLSearchParams) {
  const fbclid = urlSearchParams.get("fbclid")

  if (fbclid) {
    const creationTime = Date.now()
    const subdomainIndex = import.meta.env.MODE === "development" ? 0 : 1

    // NOTE: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/#2--format-clickid
    const fbc = `fb.${subdomainIndex}.${creationTime}.${fbclid}`

    localStorage.setItem(FACEBOOK_CLICK_ID_KEY, fbc)
  }
}

export function addGenderToLocalStorage(isFemale: boolean) {
  const gender = isFemale ? "female" : "male"
  localStorage.setItem(GENDER_KEY, gender)
}

export function getIsFemaleFromLocalStorage() {
  return localStorage.getItem(GENDER_KEY) === "female"
}

export function addAgeToLocalStorage(age: string) {
  localStorage.setItem(AGE_KEY, age)
}

export function getAgeFromLocalStorage(): string | undefined {
  return localStorage.getItem(AGE_KEY) ?? undefined
}

export function addTryFirstToLocalStorage(selection: string) {
  localStorage.setItem(TRY_FIRST_KEY, selection)
}

function getTryFirstFromLocalStorage() {
  return localStorage.getItem(TRY_FIRST_KEY)
}

// The tryFirst options store their keys
const HAIRSTYLE_KEY = "hairstyle"
const PROFESSIONAL_KEY = "professional"
const LOOKSMAXXING_KEY = "looksmaxxing"
const CREATIVE_KEY = "creative"
const CELEBRITY_KEY = "celebrity"

type TryFirstFlow =
  | "hairstyle"
  | "professional"
  | "looksmaxxing"
  | "creative"
  | "celebrity"
  | "other"

export function getTryFirstFlow(): TryFirstFlow {
  const stored = getTryFirstFromLocalStorage()
  switch (stored) {
    case HAIRSTYLE_KEY:
      return "hairstyle"
    case PROFESSIONAL_KEY:
      return "professional"
    case LOOKSMAXXING_KEY:
      return "looksmaxxing"
    case CREATIVE_KEY:
      return "creative"
    case CELEBRITY_KEY:
      return "celebrity"
    default:
      return "other"
  }
}

export function isHairstyleFlow() {
  return getTryFirstFromLocalStorage() === HAIRSTYLE_KEY
}

export function isProfessionalFlow() {
  return getTryFirstFromLocalStorage() === PROFESSIONAL_KEY
}

export function isHomeDesignFlow() {
  return getTryFirstFromLocalStorage() === "homeDesign"
}

// True when the user should see the home-design sales-page copy: either they
// came through the dedicated /onboarding/home funnel (context === "home") or
// they picked the homeDesign option on the standard tryFirst page.
export function isHomeFlow() {
  return getOnboardingContext() === "home" || isHomeDesignFlow()
}

export function setOnboardingVersion(version: string) {
  localStorage.setItem(ONBOARDING_VERSION_KEY, version)
}

export function getOnboardingVersion() {
  return localStorage.getItem(ONBOARDING_VERSION_KEY)
}

type OnboardingContext =
  | "default"
  | "cars"
  | "pets"
  | "hairstyle"
  | "looksmaxxing"
  | "home"

export function setOnboardingContext(context: OnboardingContext) {
  localStorage.setItem(ONBOARDING_CONTEXT_KEY, context)
}

export function setLooksmaxxingCustomPurchasePixelEnabled(enabled: boolean) {
  if (enabled) {
    localStorage.setItem(LOOKSMAXXING_CUSTOM_PURCHASE_PIXEL_KEY, "1")
  } else {
    localStorage.removeItem(LOOKSMAXXING_CUSTOM_PURCHASE_PIXEL_KEY)
  }
}

export function isLooksmaxxingCustomPurchasePixelEnabled() {
  return localStorage.getItem(LOOKSMAXXING_CUSTOM_PURCHASE_PIXEL_KEY) === "1"
}

export function getOnboardingContext(): OnboardingContext {
  const stored = localStorage.getItem(ONBOARDING_CONTEXT_KEY)
  switch (stored) {
    case "cars":
      return "cars"
    case "pets":
      return "pets"
    case "hairstyle":
      return "hairstyle"
    case "looksmaxxing":
      return "looksmaxxing"
    case "home":
      return "home"
    default:
      return "default"
  }
}
